import { Component, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { TreeNode } from 'primeng/api'; // PrimeNG TreeNode
import { OrganizationService } from '../../services/organization.service';
import { LocalStoreManager } from '../../services/local-store-manager.service';
import { AppTranslationService } from '../../services/app-translation.service';
import { AlertService } from '../../services/alert.service';
import { Organization } from '../../models/organization.model';
import { OrganizationLeaf } from '../../models/organizationleaf.model';
import { Company } from '../../models/company.model';
import { Factory } from '../../models/factory.model';
import { Department } from '../../models/department.model';
import { Section } from '../../models/section.model';

@Component({
  selector: 'organizationhierarchy',
  templateUrl: './organizationhierarchy.component.html',
  styleUrls: ['./organizationhierarchy.component.scss'],
})
export class OrganizationHierarchyComponent implements OnInit {
  hierarchyForm: FormGroup;
  treeData: TreeNode[] = []; // PrimeNG hierarchical data
  selectedNode: TreeNode | null = null; // Track selected node
  organizations: Organization[] = [];
  organizationLeaf: OrganizationLeaf;
  companyData = new Company();
  factoryData = new Factory();
  departmentData = new Department();
  sectionData = new Section();
  rights: number = 0;
  writeRights: number = 0;
  writeRight: boolean = true;

  constructor(
    private alertService: AlertService,
    private translationService: AppTranslationService,
    private organizationService: OrganizationService,
    private localStorage: LocalStoreManager,
    private formBuilder: FormBuilder
  ) {
    this.buildForm();
  }

  ngOnInit(): void {
    this.getOrganization();
    this.rights = this.localStorage.getDataObject('readrights');
    this.writeRights = this.localStorage.getDataObject('userrights');
    this.writeRight = (this.writeRights & 256) === 256; // Check specific rights
  }

  toggleTreeNodes(event: Event): void {
    const isChecked = (event.target as HTMLInputElement).checked;
    this.setNodeExpansion(this.treeData, isChecked);
  }

  setNodeExpansion(nodes: TreeNode[], expand: boolean): void {
    nodes.forEach((node) => {
      node.expanded = expand; // Expand or collapse the node
      if (node.children) {
        this.setNodeExpansion(node.children, expand); // Recursively expand/collapse child nodes
      }
    });
  }

  buildForm(): void {
    this.hierarchyForm = this.formBuilder.group({
      companyId: null,
      company: [{ value: null, disabled: true }],
      companyName: [{ value: null, disabled: true }],
      factoryId: null,
      factory: [{ value: null, disabled: true }],
      factoryName: [{ value: null, disabled: true }],
      departmentId: null,
      department: [{ value: null, disabled: true }],
      departmentName: [{ value: null, disabled: true }],
      sectionId: null,
      section: [{ value: null, disabled: true }],
      sectionName: [{ value: null, disabled: true }],
    });
  }

  getOrganization(): void {
    this.organizationService.getOrganization().subscribe(
      (results) => {
        this.organizations = results;
        this.fillOrganizationTree();
      },
      (error) => console.error(error)
    );
  }

  fillOrganizationTree(): void {
    const tree: TreeNode[] = [];
    let currentCompany: TreeNode | null = null;
    let currentFactory: TreeNode | null = null;
    let currentDepartment: TreeNode | null = null;

    this.organizations.forEach((org) => {
      switch (org.type) {
        case 1: // Company
          currentCompany = {
            label: `${org.companyNumber} ${org.description}`,
            data: org,
            expanded: false, // Initially collapsed
            children: [],
          };
          console.log('Tree Node:', currentCompany);
          tree.push(currentCompany);
          break;

        case 2: // Factory
          if (currentCompany) {
            currentFactory = {
              label: `${org.factoryNumber} ${org.description}`,
              data: org,
              expanded: false,
              children: [],
            };
            currentCompany.children?.push(currentFactory);
          }
          break;

        case 3: // Department
          if (currentFactory) {
            currentDepartment = {
              label: `${org.departmentNumber} ${org.description}`,
              data: org,
              expanded: false,
              children: [],
            };
            currentFactory.children?.push(currentDepartment);
          }
          break;

        case 4: // Section
          if (currentDepartment) {
            currentDepartment.children?.push({
              label: `${org.sectionNumber} ${org.description}`,
              data: org,
              expanded: false,
            });
          }
          break;
      }
    });

    this.treeData = tree; // Assign hierarchical data
  }

  selectNode(event: { node: TreeNode }): void {
    this.selectedNode = event.node;

    const selectedOrg = event.node.data as Organization;
    this.hierarchyForm.reset({}, { emitEvent: false });

    switch (selectedOrg.type) {
      case 1:
        this.hierarchyForm.get('company')?.enable();
        this.hierarchyForm.get('companyName')?.enable();
        break;
      case 2:
        this.hierarchyForm.get('factory')?.enable();
        this.hierarchyForm.get('factoryName')?.enable();
        break;
      case 3:
        this.hierarchyForm.get('department')?.enable();
        this.hierarchyForm.get('departmentName')?.enable();
        break;
      case 4:
        this.hierarchyForm.get('section')?.enable();
        this.hierarchyForm.get('sectionName')?.enable();
        break;
    }

    this.organizationService
      .getOrganizationLeaf(
        selectedOrg.companyNumber,
        selectedOrg.factoryNumber,
        selectedOrg.departmentNumber,
        selectedOrg.sectionNumber
      )
      .subscribe(
        (leaf) => {
          if (leaf) {
            this.hierarchyForm.patchValue({
              companyId: leaf.companyId,
              company: leaf.companyNumber,
              companyName: leaf.companyDesc,
              factoryId: leaf.factoryId,
              factory: leaf.factoryNumber,
              factoryName: leaf.factoryDesc,
              departmentId: leaf.departmentId,
              department: leaf.departmentNumber,
              departmentName: leaf.departmentDesc,
              sectionId: leaf.sectionId,
              section: leaf.sectionNumber,
              sectionName: leaf.sectionDesc,
            });
          }
        },
        (error) => console.error(error)
      );
  }




  // CRUD Methods for Company
  createCompany(): void {
    this.companyData.number = this.hierarchyForm.get('company')?.value;
    this.companyData.description = this.hierarchyForm.get('companyName')?.value;
    this.organizationService.createCompany(this.companyData).subscribe(() => {
      this.refreshTree();
    });
  }

  editCompany(): void {
    this.companyData.id = this.hierarchyForm.get('companyId')?.value;
    this.companyData.number = this.hierarchyForm.get('company')?.value;
    this.companyData.description = this.hierarchyForm.get('companyName')?.value;
    this.organizationService.updateCompany(this.companyData).subscribe(() => {
      this.refreshTree();
    });
  }

  deleteCompany(): void {
    const companyId = this.hierarchyForm.get('companyId')?.value;
    this.organizationService.deleteCompany(companyId)
      .subscribe(() => {
        this.refreshTree();
    });
  }

  // CRUD Methods for Factory
  createFactory(): void {
    this.factoryData.number = this.hierarchyForm.get('factory')?.value;
    this.factoryData.description = this.hierarchyForm.get('factoryName')?.value;
    this.factoryData.companyId = this.hierarchyForm.get('companyId')?.value;
    this.organizationService.createFactory(this.factoryData)
      .subscribe(() => {
        this.refreshTree();
    });
  }

  editFactory(): void {
    this.factoryData.id = this.hierarchyForm.get('factoryId')?.value;
    this.factoryData.number = this.hierarchyForm.get('factory')?.value;
    this.factoryData.description = this.hierarchyForm.get('factoryName')?.value;
    this.organizationService.updateFactory(this.factoryData).subscribe(() => {
      this.refreshTree();
    });
  }

  deleteFactory(): void {
    const companyId = this.hierarchyForm.get('companyId')?.value;
    const factoryId = this.hierarchyForm.get('factoryId')?.value;
    this.organizationService.deleteFactory(companyId, factoryId).subscribe(() => {
      this.refreshTree();
    });
  }

  // CRUD Methods for Department
  createDepartment(): void {
    this.departmentData.number = this.hierarchyForm.get('department')?.value;
    this.departmentData.description = this.hierarchyForm.get('departmentName')?.value;
    this.departmentData.factoryId = this.hierarchyForm.get('factoryId')?.value;
    this.organizationService.createDepartment(this.departmentData).subscribe(() => {
      this.refreshTree();
    });
  }

  editDepartment(): void {
    this.departmentData.id = this.hierarchyForm.get('departmentId')?.value;
    this.departmentData.number = this.hierarchyForm.get('department')?.value;
    this.departmentData.description = this.hierarchyForm.get('departmentName')?.value;
    this.organizationService.updateDepartment(this.departmentData).subscribe(() => {
      this.refreshTree();
    });
  }

  deleteDepartment(): void {
    const factoryId = this.hierarchyForm.get('factoryId')?.value;
    const departmentId = this.hierarchyForm.get('departmentId')?.value;
    this.organizationService.deleteDepartment(factoryId, departmentId).subscribe(() => {
      this.refreshTree();
    });
  }

  // CRUD Methods for Section
  createSection(): void {
    this.sectionData.number = this.hierarchyForm.get('section')?.value;
    this.sectionData.description = this.hierarchyForm.get('sectionName')?.value;
    this.sectionData.departmentId = this.hierarchyForm.get('departmentId')?.value;
    this.organizationService.createSection(this.sectionData).subscribe(() => {
      this.refreshTree();
    });
  }

  editSection(): void {
    this.sectionData.id = this.hierarchyForm.get('sectionId')?.value;
    this.sectionData.number = this.hierarchyForm.get('section')?.value;
    this.sectionData.description = this.hierarchyForm.get('sectionName')?.value;
    this.organizationService.updateSection(this.sectionData).subscribe(() => {
      this.refreshTree();
    });
  }

  deleteSection(): void {
    const departmentId = this.hierarchyForm.get('departmentId')?.value;
    const sectionId = this.hierarchyForm.get('sectionId')?.value;
    this.organizationService.deleteSection(departmentId, sectionId).subscribe(() => {
      this.refreshTree();
    });
  }

  refreshTree(): void {
    this.getOrganization(); // Reload the tree
  }

  private handleMessage(updType: number, data: string): void {
    const success = data.length === 0;
    const key = success
      ? 'notifications.UpdateRecordSucceeded'
      : 'notifications.UpdateRecordFailed';

    if (success) {
      this.alertService.successMessage(this.translationService.getTranslation(key));
    } else {
      this.alertService.error(this.translationService.getTranslation(key));
    }
  }
}
